import { filter, map, switchMap, tap } from "rxjs/operators";
import { TActionsDefinitionsList } from "./TAction";
import { action, routeAction } from "./actionFunctions";
import { Observable, bindCallback, from, of } from "rxjs";
import { PublicPropertyReportPage, TPublicPropertyReportCreateForm, TPublicPropertyReportTitleSearchResponse, TLandRegistrySearchPropertiesResponseSuccessOverride, TPublicPropertyReportAddressSearchForm, TPublicPropertyReportPageView } from "../../../../domain/codecs/form/PublicPropertyReportForm";
import { formOperation } from "../../wrappers/formOperation";
import { TStateCodec } from "../../../../domain/codecs/state";
import { TCodecLens } from "../../../../shared/src/codecs/codecLens";
import { TCodecSetState, TGetCodecState } from "../applyActions";
import { pipe } from "fp-ts/lib/function";
import { array, either, option } from "fp-ts";
import { required } from "../../../../shared/src/codecs/types/required";
import { string } from "../../../../shared/src/codecs/types/string";
import { union } from "../../../../shared/src/codecs/types/union";
import { nullCodec } from "../../../../shared/src/codecs/types/nullCodec";
import { doesErrorKeyExist } from "../../../../shared/src/codecs/errors";
import { introducerIdFromQueryString } from "../../../../domain/introducers/introducerIdFromQueryString";
import * as t from "io-ts";
import { getJson, postJson } from "../../wrappers/restRequest";
import * as FirstPartyFetchResponse from "../../../../domain/models/FirstPartyFetchResponse";

declare var grecaptcha: {
    ready: () => void,
    execute: (publicKey: string, p: {action: string}) => Promise<string>,
};

const recaptchaReady$ = () => bindCallback(grecaptcha.ready)();

const recaptchaExecute$ = () => from(grecaptcha.execute(env.REACT_APP_RECAPTCHA_PUBLIC_KEY, {action: "CreatePublicPropertyReport"}));

const LocalStorageItem = required({
    first_name: string(),
    last_name: string(),
    email: union([nullCodec(), string()]),
    phone_number: union([nullCodec(), string()]),
});

const IntroducerContextData = t.union([
    t.null,
    t.type({
        tag: t.literal("contact"),
        contact: t.type({
            introducer_id: t.string,
            introducer_name: t.string,
            first_name: t.string,
            last_name: t.string,
            email: t.union([t.null, t.string]),
            phone_number: t.union([t.null, t.string]),
        }),
    }),
    t.type({
        tag: t.literal("staff"),
        introducers: t.array(t.type({
            id: t.string,
            name: t.string,
        })),
    }),
    t.type({
        tag: t.literal("signed_in_without_introducer"),
    }),
]);

const SignInData = t.type({
    status: t.string,
});

const recaptchaExecuteForAction$ = (action: string) =>
    from(grecaptcha.execute(env.REACT_APP_RECAPTCHA_PUBLIC_KEY, { action }));

const setView = (
    view: TPublicPropertyReportPageView,
    lens: TCodecLens<TStateCodec, TStateCodec>,
    set: TCodecSetState,
) =>
    set(lens.public_property_report_page.view.set(view));

const submitPropertyReportTitleSelectForm$ = (
    addressId: number,
    lens: TCodecLens<TStateCodec, TStateCodec>,
    set: TCodecSetState,
    get: TGetCodecState,
) =>
    of(lens.public_property_report_page.form.children.results.where(({edited}) => edited.id === addressId).get()(get()))
        .pipe(
            map((form) => ({
                ...form,
                status: "submitting" as "submitting",
            })),
            tap((form) => set(lens.public_property_report_page.form.children.results.where(({edited}) => edited.id === addressId).set(form))),
            switchMap((form) => formOperation("GetPublicPropertyReportTitleNumbers", form)),
            tap((response) => set(lens.public_property_report_page.form.children.results.where(({edited}) => edited.id === addressId).set(response))),
            tap((response) => set(lens.public_property_report_page.visible_title_number.set(
                response.children.response?._tag === "success"
                    && response.children.response.results.length === 1
                    ? response.children.response.results[0].edited.title_number
                    : null,
            ))),
            switchMap((response) =>
                response.children.response?._tag === "success"
                    && response.children.response.results.length === 1
                    ? submitPropertyReportCreateForm$(
                        response.children.response.results[0].edited.title_number,
                        lens,
                        set,
                        get
                    )
                    : of(setView("title_select", lens, set))
            ),
        );

const submitPropertyReportCreateForm$ = (
    titleNumber: string,
    lens: TCodecLens<TStateCodec, TStateCodec>,
    set: TCodecSetState,
    get: TGetCodecState,
) =>
    of(lens.public_property_report_page.form.children.results.where(({edited}) => edited.id === lens.public_property_report_page.selected_address_id.get()(get())).children.response.get()(get()))
        .pipe(
            filter<TPublicPropertyReportTitleSearchResponse | null, TLandRegistrySearchPropertiesResponseSuccessOverride>((response): response is TLandRegistrySearchPropertiesResponseSuccessOverride =>
                response?._tag === "success"
                // Don't perform this request if we already have the response for it
                && pipe(
                    response.results,
                    array.filter((result) =>
                        result.edited.title_number === titleNumber
                        && result.children.response !== null
                    ),
                ).length === 0
            ),
            map((response) =>
                pipe(
                    response.results,
                    array.map((result) =>
                        result.edited.title_number === titleNumber
                            ? {
                                ...result,
                                status: "submitting" as "submitting",
                            }
                            : result,
                    ),
                    (results) => ({
                        ...response,
                        results,
                    }),
                )
            ),
            tap((response) => set(lens.public_property_report_page.form.children.results.where(({edited}) => edited.id === lens.public_property_report_page.selected_address_id.get()(get())).children.response.set(response))),
            tap(() => setView("results", lens, set)),
            map(() => lens.public_property_report_page.form.children.results.where(({edited}) => edited.id === lens.public_property_report_page.selected_address_id.get()(get())).children.response.get()(get())),
            map((response) =>
                response?._tag === "success"
                    ? pipe(
                        response.results,
                        array.findFirst((result) => result.edited.title_number === titleNumber)
                    )
                    : option.none,
            ),
            switchMap((formOption) =>
                pipe(
                    formOption,
                    option.fold<TPublicPropertyReportCreateForm, Observable<undefined | TPublicPropertyReportTitleSearchResponse | null>>(
                        () => of(undefined),
                        (form) =>
                            recaptchaReady$()
                                .pipe(
                                    switchMap(recaptchaExecute$),
                                    switchMap((recaptcha_token) => formOperation("CreatePublicPropertyReport", {
                                        ...form,
                                        edited: {
                                            ...form.edited,
                                            recaptcha_token,
                                        },
                                    })),
                                    map((r) =>
                                        pipe(
                                            lens.public_property_report_page.form.children.results.where(({edited}) => edited.id === lens.public_property_report_page.selected_address_id.get()(get())).children.response.get()(get()),
                                            (response) =>
                                                response?._tag === "success"
                                                    ? pipe(
                                                        response.results,
                                                        array.map((result) =>
                                                            result.edited.title_number === titleNumber
                                                                ? {
                                                                    ...r,
                                                                    edited: r.status === "success" ? r.edited : result.edited,
                                                                }
                                                                : result,
                                                        ),
                                                        (results) => ({
                                                            ...response,
                                                            results,
                                                        }),
                                                    )
                                                    : response,
                                        )
                                    ),
                                    tap((response) => set(lens.public_property_report_page.form.children.results.where(({edited}) => edited.id === lens.public_property_report_page.selected_address_id.get()(get())).children.response.set(response))),
                                    tap(() =>
                                        form.edited.introducer.email
                                            ? localStorage.setItem("public_property_report_user", JSON.stringify({
                                                first_name: form.edited.introducer.first_name,
                                                last_name: form.edited.introducer.last_name,
                                                email: form.edited.introducer.email,
                                                phone_number: form.edited.introducer.phone_number,
                                            }))
                                            : undefined,
                                    ),
                                )
                    )
                )
            ),
        );

const submitAddressSearchForm$ = (
    form: TPublicPropertyReportAddressSearchForm,
    lens: TCodecLens<TStateCodec, TStateCodec>,
    set: TCodecSetState,
    get: TGetCodecState,
) =>
    of(form)
        .pipe(
            map((f) => lens.public_property_report_page.form.set({
                ...f,
                status: "submitting",
            })),
            tap(set),
            switchMap(() => formOperation("GetPublicPropertyReportAddressSearch", lens.public_property_report_page.form.get()(get()))),
            tap((response) => set(lens.public_property_report_page.form.set(response))),
            tap((response) => set(lens.public_property_report_page.selected_address_id.set(
                response.children.results.length === 1
                ? response.children.results[0].edited.id
                : null,
            ))),
            switchMap((response) =>
                response.status === "success" && response.children.results.length === 1 ? submitPropertyReportTitleSelectForm$(
                    response.children.results[0].edited.id,
                    lens,
                    set,
                    get
                )
                : response.status === "success" ? of(setView("address_select", lens, set))
                : (
                    response.status === "validationError"
                    && (
                        doesErrorKeyExist("edited.deceased_name", response.validationErrors)
                        || doesErrorKeyExist("edited.date_of_death", response.validationErrors)
                        || doesErrorKeyExist("edited.building_number_name", response.validationErrors)
                        || doesErrorKeyExist("edited.postcode", response.validationErrors)
                        || doesErrorKeyExist("edited.introducer_external_reference_id", response.validationErrors)
                        || doesErrorKeyExist("edited.introducer.first_name", response.validationErrors)
                        || doesErrorKeyExist("edited.introducer.last_name", response.validationErrors)
                        || doesErrorKeyExist("edited.introducer.email", response.validationErrors)
                        || doesErrorKeyExist("edited.introducer.phone_number", response.validationErrors)
                        || doesErrorKeyExist("edited.introducer_notes", response.validationErrors)
                    )
                ) ? of(setView("form_initial", lens, set))
                : (
                    response.status === "validationError"
                    && (
                        doesErrorKeyExist("edited.executor_or_beneficiary.is_executor", response.validationErrors)
                        || doesErrorKeyExist("edited.executor_or_beneficiary.first_name", response.validationErrors)
                        || doesErrorKeyExist("edited.executor_or_beneficiary.last_name", response.validationErrors)
                        || doesErrorKeyExist("edited.executor_or_beneficiary.email", response.validationErrors)
                        || doesErrorKeyExist("edited.executor_or_beneficiary.phone_number", response.validationErrors)
                    )
                ) ? of(setView("form_executor_or_beneficiary", lens, set))
                : of(setView("form_initial", lens, set))
            ),
        );

export const actions: TActionsDefinitionsList = [
    routeAction("VIEW_PROBATE_PROPERTY_REPORT_PAGE", (obs$, lens, set, get) => {
        obs$
            .pipe(
                map((payload) => lens.public_property_report_page.set({
                    ...PublicPropertyReportPage.newDefault(),
                    form: {
                        ...PublicPropertyReportPage.newDefault().form,
                        edited: {
                            ...PublicPropertyReportPage.newDefault().form.edited,
                            introducer_id: introducerIdFromQueryString(payload.queryStringParams.introducer_id),
                            introducer: {
                                ...PublicPropertyReportPage.newDefault().form.edited.introducer,
                                ...pipe(
                                    localStorage.getItem("public_property_report_user"),
                                    (localStorageValue) => JSON.parse(localStorageValue || "{}"),
                                    LocalStorageItem.decode,
                                    either.getOrElse(LocalStorageItem.newDefault),
                                ),
                            },
                        },
                    },
                    view: "form_initial",
                })),
                tap(set),
                switchMap(() =>
                    from(getJson({
                        url: "/v1/public/introducer-context",
                        dataCodec: IntroducerContextData,
                        defaultData: null,
                    })())
                ),
                tap((response) => {
                    const context =
                        response.tag === FirstPartyFetchResponse.constants.STATUS_2XX
                            ? response.response.data
                            : null;

                    if (context === null) {
                        return;
                    }

                    const page = lens.public_property_report_page.get()(get());

                    if (context.tag === "staff") {
                        set(lens.public_property_report_page.set({
                            ...page,
                            staff_introducer_options: context.introducers,
                            view: "staff_introducer_select",
                            form: {
                                ...page.form,
                                edited: {
                                    ...page.form.edited,
                                    introducer_id: null,
                                },
                            },
                        }));
                        return;
                    }

                    if (context.tag === "signed_in_without_introducer") {
                        set(lens.public_property_report_page.set({
                            ...page,
                            view: "signed_in_without_introducer",
                        }));
                        return;
                    }

                    set(lens.public_property_report_page.set({
                        ...page,
                        signed_in_contact: context.contact,
                        form: {
                            ...page.form,
                            edited: {
                                ...page.form.edited,
                                introducer_id: context.contact.introducer_id,
                                introducer: {
                                    ...page.form.edited.introducer,
                                    first_name: context.contact.first_name || page.form.edited.introducer.first_name,
                                    last_name: context.contact.last_name || page.form.edited.introducer.last_name,
                                    email: context.contact.email ?? page.form.edited.introducer.email,
                                    phone_number: context.contact.phone_number ?? page.form.edited.introducer.phone_number,
                                },
                            },
                        },
                    }));
                }),
            ).subscribe();
    }),
    action("PUBLIC_PROPERTY_REPORT_STAFF_INTRODUCER_FILTER_CHANGE", (obs$: Observable<string>, lens, set, get) => {
        obs$
            .pipe(
                map(lens.public_property_report_page.staff_introducer_filter.set),
                tap(set),
            )
            .subscribe();
    }),
    action("PUBLIC_PROPERTY_REPORT_STAFF_INTRODUCER_SELECTED", (obs$: Observable<string>, lens, set, get) => {
        obs$
            .pipe(
                tap((introducerId) => {
                    const page = lens.public_property_report_page.get()(get());

                    set(lens.public_property_report_page.set({
                        ...page,
                        selected_staff_introducer_id: introducerId,
                        view: "form_initial",
                        form: {
                            ...page.form,
                            edited: {
                                ...page.form.edited,
                                introducer_id: introducerId,
                            },
                        },
                    }));
                }),
            )
            .subscribe();
    }),
    action("PUBLIC_PROPERTY_REPORT_STAFF_INTRODUCER_CLEARED", (obs$: Observable<undefined>, lens, set, get) => {
        obs$
            .pipe(
                tap(() => {
                    const page = lens.public_property_report_page.get()(get());

                    set(lens.public_property_report_page.set({
                        ...page,
                        selected_staff_introducer_id: null,
                        staff_introducer_filter: "",
                        view: "staff_introducer_select",
                        form: {
                            ...page.form,
                            edited: {
                                ...page.form.edited,
                                introducer_id: null,
                            },
                        },
                    }));
                }),
            )
            .subscribe();
    }),
    action("PUBLIC_PROPERTY_REPORT_SIGN_IN_EMAIL_CHANGE", (obs$: Observable<string>, lens, set, get) => {
        obs$
            .pipe(
                map(lens.public_property_report_page.sign_in_email.set),
                tap(set),
            )
            .subscribe();
    }),
    action("PUBLIC_PROPERTY_REPORT_SIGN_IN_SUBMIT", (obs$: Observable<undefined>, lens, set, get) => {
        obs$
            .pipe(
                tap(() => set(lens.public_property_report_page.sign_in_status.set("submitting"))),
                switchMap(() =>
                    recaptchaReady$().pipe(
                        switchMap(() => recaptchaExecuteForAction$("IntroducerSignIn")),
                    )
                ),
                switchMap((recaptchaToken) =>
                    from(postJson({
                        url: "/v1/public/introducer-sign-in",
                        body: {
                            email: lens.public_property_report_page.sign_in_email.get()(get()),
                            recaptcha_token: recaptchaToken,
                        },
                        dataCodec: SignInData,
                        defaultData: { status: "domain_not_recognised" },
                    })())
                ),
                tap((response) =>
                    set(lens.public_property_report_page.sign_in_status.set(
                        response.tag === FirstPartyFetchResponse.constants.STATUS_2XX
                        && response.response.data.status === "link_sent"
                            ? "link_sent"
                            : "domain_not_recognised"
                    ))
                ),
            )
            .subscribe();
    }),
    action("PUBLIC_PROPERTY_REPORT_ADDRESS_SEARCH_CHANGE", (obs$: Observable<TPublicPropertyReportAddressSearchForm>, lens, set, get) => {
        obs$
            .pipe(
                map(lens.public_property_report_page.form.set),
                tap(set),
            )
            .subscribe();
    }),
    action("PUBLIC_PROPERTY_REPORT_ADDRESS_SEARCH_SUBMIT", (obs$: Observable<undefined>, lens, set, get) => {
        obs$
            .pipe(
                switchMap(() => submitAddressSearchForm$(lens.public_property_report_page.form.get()(get()), lens, set, get)),
            )
            .subscribe();
    }),
    action("PUBLIC_PROPERTY_REPORT_SELECTED_ADDRESS_ID_CHANGE", (obs$: Observable<number>, lens, set, get) => {
        obs$
            .pipe(
                tap((addressId) => set(lens.public_property_report_page.selected_address_id.set(addressId))),
                switchMap((addressId) =>
                    submitPropertyReportTitleSelectForm$(
                        addressId,
                        lens,
                        set,
                        get
                    ),
                ),
            )
            .subscribe();
    }),
    action("PUBLIC_PROPERTY_REPORT_VISIBLE_TITLE_NUMBER_CHANGE", (obs$: Observable<string>, lens, set, get) => {
        obs$
            .pipe(
                tap((titleNumber) => set(lens.public_property_report_page.visible_title_number.set(titleNumber))),
                switchMap((titleNumber) =>
                    submitPropertyReportCreateForm$(
                        titleNumber,
                        lens,
                        set,
                        get
                    ),
                ),
            )
            .subscribe();
    }),
    action("PUBLIC_PROPERTY_REPORT_INITIAL_FORM_CONTINUE_PRESSED", (obs$: Observable<undefined>, lens, set, get) => {
        obs$
            .pipe(
                map(() => lens.public_property_report_page.form.get()(get())),
                switchMap((form) =>
                    form.edited.transaction_type !== null
                        ? submitAddressSearchForm$(form, lens, set, get)
                        : of(setView("form_transaction_type", lens, set))
                ),
            )
            .subscribe();
    }),
    action("PUBLIC_PROPERTY_REPORT_TRANSACTION_TYPE_CHANGE", (obs$: Observable<TPublicPropertyReportAddressSearchForm>, lens, set, get) => {
        obs$
            .pipe(
                map((form) => ({
                    ...form,
                    edited: {
                        ...form.edited,
                        wants_valuation: true,
                    }
                })),
                switchMap((form) =>
                    of(set(lens.public_property_report_page.form.set(form)))
                        .pipe(
                            tap(() => setView("form_executor_or_beneficiary", lens, set))
                        )
                ),
            )
            .subscribe();
    }),
];
